"use professional"
import { useState, useEffect } from "react"
import Select from 'react-select'
import { useAuth } from "../../AuthContext" // Adjust the import path as necessary
import { useNavigate } from "react-router-dom"
import { useLanguage } from "../../contexts/LanguageContext";

const itFields = [
  "Computer Science",
  "Software Engineering",
  "Information Technology",
  "Cybersecurity",
  "Data Science",
  "Artificial Intelligence",
  "Web Development",
  "Network Engineering",
  "Cloud Computing",
  "Database Management",
  "Mobile App Development",
  "Game Development",
  "IT Project Management",
  "Computer Engineering",
  "Graphic Design",
]

function EducationForm({ existingRecords = [], onSubmitSuccess }) {
  const [recordCount, setRecordCount] = useState(existingRecords.length || 1); // Default to 1 for adding
  const [educationRecords, setEducationRecords] = useState([]);
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const proId = user?.secondId || 0; 




  const fieldOptions = itFields.map(field => ({
    value: field,
    label: field.charAt(0).toUpperCase() + field.slice(1)
  }));

  useEffect(() => {
    if (existingRecords.length > 0) {
      // Map existing records to match the expected field names
      console.log("Existing Records:", existingRecords);
      const mappedRecords = existingRecords.map(record => ({
        institution: record.institution_name || "", // Map to match the form field
        degree: record.degree || "",
        fieldOfStudy: record.field_of_study || "",
        startDate: record.start_date || "",
        endDate: record.end_date || "",
        notes: record.notes || "",
        eduId: record.education_id|| "",
      }));
      setEducationRecords(mappedRecords);
    } else {
      setEducationRecords(Array(recordCount).fill({
        institution: "",
        degree: "",
        fieldOfStudy: "",
        startDate: "",
        endDate: "",
        notes: "",
      }));
    }
  }, [existingRecords, recordCount]);

  const handleRecordCountChange = (e) => {
    const count = Number.parseInt(e.target.value, 10);
    setRecordCount(count);
    setEducationRecords(Array(count).fill({})); // Reset records when count changes
  };

  const handleInputChange = (index, field, value) => {
    const updatedRecords = [...educationRecords];
    updatedRecords[index] = { ...updatedRecords[index], [field]: value };
    setEducationRecords(updatedRecords);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        const url = existingRecords.length > 0 
          ? `http://localhost:5000/update-education` 
          : `http://localhost:5000/education/${proId}`; // Replace :${proId} with ${proId}
        const method = existingRecords.length > 0 ? "PUT" : "POST";
        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ educationRecords }),
        });
        if (response.ok) {
          alert(existingRecords.length > 0 ? t('educationUpdatedSuccess') : t('educationSubmittedSuccess'));
          if (onSubmitSuccess) onSubmitSuccess();
          navigate("/ProProject");
        } else {
          alert(t('educationSubmitFailed'));
        }
      } catch (error) {
        console.error("Error submitting education details:", error);
        alert(t('educationSubmitError'));
      }
    }
  };

  const validateForm = () => {
    return educationRecords.every(
      (record) => record.institution && record.degree && record.fieldOfStudy && record.startDate && record.endDate,
    );
  };

  const inputStyle = {
    background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--input-border)',
    borderRadius: '10px', padding: '10px 14px', width: '100%', outline: 'none', fontSize: '0.9rem',
    transition: 'border-color 0.2s, background 0.2s',
  };
  const labelStyle = { display: 'block', color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, marginBottom: '6px' };

  const selectStyles = {
    control: (base) => ({ ...base, background: 'var(--input-bg)', borderColor: 'var(--input-border)', borderRadius: '10px', minHeight: '42px', boxShadow: 'none', '&:hover': { borderColor: 'rgba(var(--accent-primary-rgb), 0.5)' } }),
    menu: (base) => ({ ...base, background: 'var(--bg-card-solid)', border: '1px solid var(--border-glass)', borderRadius: '10px' }),
    option: (base, state) => ({ ...base, background: state.isFocused ? 'var(--bg-elevated)' : 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }),
    singleValue: (base) => ({ ...base, color: 'var(--text-primary)' }),
    input: (base) => ({ ...base, color: 'var(--text-primary)' }),
    placeholder: (base) => ({ ...base, color: 'var(--text-muted)' }),
  };

  return (
    <div style={{ maxWidth: '720px', margin: '40px auto', background: 'var(--bg-card)', borderRadius: '20px', border: '1px solid var(--border-glass)', padding: '40px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #10b981, var(--accent-violet))' }} />
      <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', marginBottom: '8px' }}>{existingRecords.length > 0 ? t('editEducationDetails') : t('addEducationDetails')}</h2>
      {!existingRecords.length && (
        <div className="mb-4">
          <label htmlFor="recordCount" style={labelStyle}>{t('howManyEducationRecords')} <span style={{ color: '#ef4444' }}>*</span></label>
          <input type="number" style={inputStyle} id="recordCount" min="1" placeholder={t('educationRecordsPlaceholder')} required value={recordCount} onChange={handleRecordCountChange}
            onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
        </div>
      )}
      <form id="educationForm" className="grid gap-6" onSubmit={handleSubmit}>
        {educationRecords.map((record, index) => (
          <div key={index} style={{ background: 'var(--bg-glass)', borderRadius: '14px', border: '1px solid var(--border-glass)', padding: '24px', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>{t('education')} {index + 1}</h3>
            <div className="mb-4">
              <label htmlFor={`institution${index}`} style={labelStyle}>{t('institutionName')} <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" style={inputStyle} id={`institution${index}`} placeholder={t('institutionNamePlaceholder')} required value={record.institution || ""}
                onChange={(e) => handleInputChange(index, "institution", e.target.value)}
                onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
            </div>
            <div className="mb-4">
              <label htmlFor={`degree${index}`} style={labelStyle}>{t('degreeCertificateName')} <span style={{ color: '#ef4444' }}>*</span></label>
              <select style={inputStyle} id={`degree${index}`} required value={record.degree || ""} onChange={(e) => handleInputChange(index, "degree", e.target.value)}>
                <option value="">{t('selectDegreeCertificate')}</option>
                <option value="bachelor">{t('bachelorsDegree')}</option>
                <option value="master">{t('mastersDegree')}</option>
                <option value="phd">Ph.D.</option>
                <option value="diploma">{t('diploma')}</option>
                <option value="certificate">{t('certificate')}</option>
              </select>
            </div>
            <div className="mb-4">
              <label htmlFor={`fieldOfStudy${index}`} style={labelStyle}>{t('fieldOfStudy')} <span style={{ color: '#ef4444' }}>*</span></label>
              <Select options={fieldOptions}
                value={fieldOptions.find(option => option.value === record.fieldOfStudy)}
                onChange={(selectedOption) => handleInputChange(index, 'fieldOfStudy', selectedOption.value)}
                styles={selectStyles} className="w-full" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor={`startDate${index}`} style={labelStyle}>{t('startDate')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="date" style={inputStyle} id={`startDate${index}`} required value={record.startDate || ""}
                  onChange={(e) => handleInputChange(index, "startDate", e.target.value)}
                  onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
              </div>
              <div className="mb-4">
                <label htmlFor={`endDate${index}`} style={labelStyle}>{t('endDate')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="date" style={inputStyle} id={`endDate${index}`} required value={record.endDate || ""}
                  onChange={(e) => handleInputChange(index, "endDate", e.target.value)}
                  onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor={`notes${index}`} style={labelStyle}>{t('additionalNotesOptional')}</label>
              <textarea style={{ ...inputStyle, resize: 'vertical' }} id={`notes${index}`} rows="3" placeholder={t('additionalNotesPlaceholder')} value={record.notes || ""}
                onChange={(e) => handleInputChange(index, "notes", e.target.value)}
                onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
            </div>
          </div>
        ))}
        <button type="submit" disabled={!validateForm()}
          style={{ width: '100%', background: validateForm() ? 'linear-gradient(135deg, #10b981, #059669)' : 'var(--bg-elevated)', color: validateForm() ? '#fff' : 'var(--text-muted)', border: 'none', padding: '12px 20px', borderRadius: '10px', fontWeight: 700, cursor: validateForm() ? 'pointer' : 'not-allowed', fontSize: '0.95rem', transition: 'opacity 0.2s', marginTop: '8px' }}
          onMouseEnter={e => { if(validateForm()) e.currentTarget.style.opacity = '0.85' }} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
          {existingRecords.length > 0 ? t('updateEducationDetails') : t('saveEducationDetails')}
        </button>
      </form>
    </div>
  );
}

export default EducationForm;

